import type { FC } from 'react'

import { buildPath } from '@rentspree/path'
// eslint-disable-next-line no-use-before-define
import React from 'react'

import {
  SHARE_EMAIL_BODY,
  SHARE_EMAIL_SUBJECT,
  SUPPORTED_SOCIAL_MEDIA,
  SHARE_TWITTER_TEXT,
  FACEBOOK_SHARE_URL,
  TWITTER_SHARE_URL,
  LINKEDIN_SHARE_URL,
} from 'constant'
import tracker, { events } from 'tracker'
import { openLink, openNewWindow } from 'utils/route'

import { ShareButton } from './share-button'
import {
  SocialSharingContainer,
  SocialSharingLabel,
  SocialSharingList,
  SocialSharingListItem,
} from './social-sharing.styled'

export type SocialMedia = typeof SUPPORTED_SOCIAL_MEDIA[number]
export type SocialSharingProps = {
  url: string
  propertyAddress: string
  onClick?: ({ type: SocialMedia, url: string }) => void
  show?: SocialMedia[]
  disable?: boolean
  style?: React.CSSProperties
}

const openShareWindow = (url: string, type: SocialMedia, propertyAddress: string) => {
  switch (type) {
    case 'facebook':
      openNewWindow(buildPath(FACEBOOK_SHARE_URL, {}, { u: url }))
      break
    case 'twitter':
      openNewWindow(buildPath(TWITTER_SHARE_URL, {}, { url, text: SHARE_TWITTER_TEXT }))
      break
    case 'linkedin':
      openNewWindow(buildPath(LINKEDIN_SHARE_URL, {}, { url }))
      break
    case 'email':
      openLink(
        buildPath(
          'mailto:',
          {},
          {
            subject: SHARE_EMAIL_SUBJECT,
            body: SHARE_EMAIL_BODY.replaceAll('{Property Address}', propertyAddress).replaceAll(
              '{Listing page URL}',
              url,
            ),
          },
        ),
      )
      break
    default:
      break
  }
}

const SocialSharing: FC<SocialSharingProps> = ({
  url,
  onClick,
  propertyAddress,
  show = [],
  disable = false,
  style,
}) => {
  const handleClick = (type: SocialMedia) => () => {
    if (disable) return
    onClick?.({ type, url })
    tracker?.trackEvent(events.LISTING_PUBLIC_PAGE.SHARE_LISTING_PAGE, {
      share_to: type,
      click_from: 'public_page',
    })
    openShareWindow(url, type, propertyAddress)
  }

  if (!show || show?.length <= 0) {
    return null
  }

  return (
    <SocialSharingContainer style={style}>
      <SocialSharingLabel>Share listing:</SocialSharingLabel>
      <SocialSharingList>
        {React.Children.toArray(
          SUPPORTED_SOCIAL_MEDIA.map((socialMediaName) => {
            const isSupported = show?.indexOf(socialMediaName) !== -1
            if (!isSupported) {
              return null
            }

            return (
              <SocialSharingListItem>
                <ShareButton
                  type={socialMediaName}
                  onClick={handleClick(socialMediaName)}
                  disable={disable}
                />
              </SocialSharingListItem>
            )
          }),
        )}
      </SocialSharingList>
    </SocialSharingContainer>
  )
}

export { SocialSharing }
